/**
 * SageRevenueMargin — revenue bars with the gross-margin line over them, from the served
 * Sage cells. Split out of MarginBody on the CD-1 budget when the axis learned to stop at
 * the last month that CLOSED.
 *
 * A month still running plots nothing (kit/trendSeries), so the axis ends at the last
 * complete month and the header says which one. Group revenue was £710k in July and £4.2k
 * on the 19th of August: drawn at face value, the line fell off its own axis.
 */

import React from 'react'
import BarLineChart from './BarLineChart'
import { useTokens } from './useTokens'
import { buildTrendSeries } from '../trendSeries'
import { periodHeading } from '../cellFormat'
import type { ResolveResponse, Site } from '../../../types/metrics'

const CARD = 'border border-line rounded-xl bg-panel overflow-hidden'
const HEAD = 'flex items-center gap-2.5 px-4 py-3 border-b border-line2 font-semibold text-[13.5px] text-ink'

/**
 * Revenue paired with its GM point, ACTUAL CLOSED MONTHS ONLY.
 *
 * ⚠ A FORECAST IS NOT AN ACTUAL, and this header says "Sage actuals" (23 Aug 2026). The
 * filter used to be `actual !== null` alone, which let Revenue's forecast months through:
 * scoped to 2026 the card read "complete months to Dec 26" over five bars Sage has not
 * posted, beside a P&L bridge covering January to July. Two periods, one page — the exact
 * defect this panel sits next to.
 *
 * Only `revenue` carries a forecast, so only this chart could show it. `cell.forecast` is the
 * server's word for it and is never re-derived here.
 */
function servedMonths(data: ResolveResponse, site: Site) {
  const rev = buildTrendSeries(data, 'revenue', site)
  const gpm = buildTrendSeries(data, 'gpm', site)
  return rev.points
    .map((p, i) => ({ p, g: gpm.points[i] }))
    .filter((x) => x.p.actual !== null && !x.p.forecast)
}

export default function SageRevenueMargin({
  data,
  site,
}: {
  data: ResolveResponse
  site: Site
}): React.JSX.Element {
  const t = useTokens()
  const served = servedMonths(data, site)
  const bars = served.map((x) => ({ v: x.p.actual as number, label: x.p.label, color: t.orange }))
  const line = served.map((x) => x.g.actual ?? 0)
  const last = served[served.length - 1]
  return (
    <div className={CARD}>
      <div className={HEAD}>
        Revenue &amp; gross margin — bar + line{' '}
        <span className="font-mono text-[10px] font-normal text-mut2">
          Sage actuals · {last ? `complete months to ${periodHeading(last.p.period)}` : 'no complete months'}
        </span>
      </div>
      <div className="px-2 py-3">
        {bars.length ? (
          <BarLineChart
            bars={bars}
            line={line}
            lineColor={t.purple}
            barName="Revenue"
            lineName="Gross margin"
            fmtBar={(v) => `£${Math.round(v)}k`}
            fmtLine={(v) => `${v.toFixed(0)}%`}
          />
        ) : (
          <div className="px-4 py-8 text-center text-sm text-mut2">No served months in this window.</div>
        )}
      </div>
    </div>
  )
}
